import { Check, X } from "lucide-react";
import {
  PASSWORD_REQUIREMENTS,
  checkPasswordRequirements,
  passwordStrengthScore,
} from "@nh/shared";
import { cn } from "@/shared/lib/cn";

/** Cuántos requisitos hay. Sale del contrato, no de un número escrito aquí. */
const MAX_SCORE = PASSWORD_REQUIREMENTS.length;

export interface PasswordStrengthProps {
  /** Lo que la persona lleva escrito. */
  value: string;
  /**
   * `id` del bloque, para que el campo lo enlace por `aria-describedby`.
   * Sin esto el medidor es un adorno visual que un lector de pantalla no
   * asocia con el campo que está describiendo.
   */
  describedById?: string;
  /**
   * Si se despliega la lista de requisitos. `true` por defecto.
   *
   * Quien decide es el campo, no este componente: la regla depende del
   * foco, que aquí no se conoce. Al plegarse la lista NO se desmonta ni
   * se marca `aria-hidden` — sigue colgando del `aria-describedby`, así
   * que un lector de pantalla la oye al enfocar el campo aunque a la
   * vista no ocupe sitio. Plegar es una decisión de espacio, no de
   * quitarle la información a nadie.
   */
  showRequirements?: boolean;
  className?: string;
}

interface Level {
  /** El resumen de una palabra. */
  label: string;
  /** Color de la barra. NO es texto: aquí manda el 3:1 de componentes. */
  bar: string;
  /** Color del resumen. Texto pequeño: manda el 4.5:1. */
  text: string;
}

/**
 * Los cuatro niveles.
 *
 * ── Por qué el color del texto no sigue al de la barra ────────────────
 *
 * La card de vidrio de las pantallas de acceso se comporta como #E9E4DC.
 * Medido sobre ese fondo, la rampa de tierra del sistema da:
 *
 *   ds-terracotta  #9F6055 → 3.88:1
 *   ds-amber-soft  #C69C8A → 1.95:1
 *   ds-success     #3E9B6F → 2.71:1
 *
 * Ninguno llega a 4.5:1, el mínimo para texto pequeño. Sirven para la
 * BARRA —que es un componente, no texto, y además es redundante: el mismo
 * estado va escrito al lado y en `aria-valuenow`— pero no para leerse.
 *
 * El resumen usa entonces la rampa oscura del sistema, toda por encima
 * de 4.5:1 sobre ese mismo fondo:
 *
 *   ds-bad-t  #6F3A2E → 7.15:1   (débil)
 *   ds-ink-3  #3A2F55 → 9.66:1   (neutro y medio)
 *   #2b6b4c            → 5.01:1   (fuerte — el mismo verde oscuro que ya
 *                                  usa `Alert variant="success"` para su
 *                                  texto, por la misma razón)
 *
 * `ds-ink-4` (#8A8377) da 2.97:1 y por eso NO aparece en ningún texto de
 * este archivo, ni siquiera en los requisitos que faltan.
 */
const LEVELS = {
  empty: { label: "Escribe una contraseña", bar: "", text: "text-ds-ink-3" },
  weak: { label: "Débil", bar: "bg-ds-terracotta", text: "text-ds-bad-t" },
  medium: { label: "Media", bar: "bg-ds-amber-soft", text: "text-ds-ink-3" },
  strong: { label: "Fuerte", bar: "bg-ds-success", text: "text-[#2b6b4c]" },
} satisfies Record<string, Level>;

/**
 * Qué nivel corresponde a un score. Se deriva de `MAX_SCORE` en vez de
 * mapear 0→0, 1→1… para que añadir un requisito al contrato no descuadre
 * silenciosamente el medidor: cumplirlos todos siempre es "Fuerte" y a uno
 * de terminar siempre es "Media".
 */
function levelFor(score: number): Level {
  if (score <= 0) return LEVELS.empty;
  if (score >= MAX_SCORE) return LEVELS.strong;
  if (score >= MAX_SCORE - 1) return LEVELS.medium;
  return LEVELS.weak;
}

/**
 * Medidor de fuerza de contraseña.
 *
 * Los requisitos NO se escriben aquí: salen de `checkPasswordRequirements`
 * de `@nh/shared`, la misma función de la que sale el esquema que valida
 * en el servidor y los mensajes de error del formulario. Un medidor con su
 * propia lista puede decir "fuerte" sobre algo que el API rechaza, y eso
 * es peor que no tener medidor: la persona escribe algo que el sistema le
 * aprobó a la vista y luego se lo niega al enviar.
 *
 * A propósito SIN `aria-live`: el bloque cambia en cada tecla y anunciarlo
 * al vuelo convierte escribir una contraseña en una metralleta de voz. El
 * enlace correcto es `aria-describedby` desde el campo (ver
 * `describedById`), que un lector de pantalla lee al enfocar y al pedirlo.
 */
export function PasswordStrength({
  value,
  describedById,
  showRequirements = true,
  className,
}: PasswordStrengthProps) {
  const requirements = checkPasswordRequirements(value);
  const score = passwordStrengthScore(value);
  const level = levelFor(score);
  const percent = MAX_SCORE === 0 ? 0 : Math.round((score / MAX_SCORE) * 100);
  const complete = score >= MAX_SCORE;

  return (
    <div id={describedById} className={cn("grid gap-2", className)}>
      <div
        role="progressbar"
        aria-label="Fuerza de la contraseña"
        aria-valuemin={0}
        aria-valuemax={MAX_SCORE}
        aria-valuenow={score}
        aria-valuetext={level.label}
        className="h-2 w-full overflow-hidden rounded-ds-pill bg-ds-mist ring-1 ring-inset ring-ds-border-strong"
      >
        <div
          className={cn(
            "h-full rounded-ds-pill transition-[width,background-color] duration-300 ease-out",
            "motion-reduce:transition-none",
            level.bar,
          )}
          style={{ width: `${percent}%` }}
        />
      </div>

      {/*
        Resumen y cuenta en un solo renglón. La cuenta existe para que el
        avance se lea sin recorrer la lista — y sobre todo para cuando la
        lista está plegada, que es cuando la barra se queda sola.
      */}
      <div className="flex items-baseline justify-between gap-2">
        <p className={cn("flex items-center gap-1.5 text-xs font-semibold", level.text)}>
          {complete && <Check className="h-4 w-4 shrink-0" aria-hidden="true" />}
          {level.label}
        </p>
        {value.length > 0 && (
          <p className="text-[11px] tabular-nums text-ds-ink-3">
            {score} de {MAX_SCORE}
          </p>
        )}
      </div>

      {/*
        El plegado anima `grid-template-rows` de 0fr a 1fr: es lo que
        permite una transición de altura suave sin medir el contenido ni
        fijar un alto a mano que se rompa al añadir un requisito.
      */}
      <div
        className={cn(
          "grid transition-[grid-template-rows,opacity] duration-200 ease-out",
          "motion-reduce:transition-none",
          showRequirements ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0",
        )}
      >
        <ul className="grid gap-1 overflow-hidden">
          {requirements.map((requirement) => (
            <li
              key={requirement.id}
              className={cn(
                "flex items-center gap-1.5 text-xs",
                requirement.met ? "text-[#2b6b4c]" : "text-ds-ink-3",
              )}
            >
              {requirement.met ? (
                <Check className="h-4 w-4 shrink-0" aria-hidden="true" />
              ) : (
                <X className="h-4 w-4 shrink-0" aria-hidden="true" />
              )}
              <span>{requirement.label}</span>
              {/*
                El icono solo no comunica: quien no ve la palomita necesita
                oír el estado. Va pegado al label y no como `aria-label` del
                icono para que se lea en el orden natural de la frase.
              */}
              <span className="sr-only">
                {requirement.met ? "· cumplido" : "· pendiente"}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
